import {DateValueLineGraphLoadingSmall} from '#/atoms/graphs/date-value-line-graph';
import {LoadingSection} from '#/atoms/loading-section';
import {LandMarketPriceGraph} from '#/components/graphs/land-market-price-graph';
import {LandMarketIndicator} from '#/components/indicators/market-indicator';
import {TransLandCityStateText} from '#/components/land/land-city-state-text';
import {LandShapeImage, LandThumbnail} from '#/components/lands/land-thumbnail';
import {SyntheticMarketDataRange, syntheticMarketDataRangeToGraphDateType} from '#/types/synthetic-market-data-range';
import {ClassName} from '#/utils/utils';
import {Land, LandMarketInfo} from '@shareland/shareland-common';
import {FunctionComponent} from 'react';

type LandIconInfoMarketTileProps = {
  land: Land;
  range: SyntheticMarketDataRange;
  landMarketInfo?: LandMarketInfo;
  thumbnailMode?: 'empty' | 'image' | 'shape';
};
export const LandIconInfoMarketTile: FunctionComponent<LandIconInfoMarketTileProps & ClassName> = ({
  className,
  land,
  range,
  landMarketInfo,
  thumbnailMode = 'empty',
}) => {
  return (
    <div className={`flex items-center gap-5 ${className}`}>
      {thumbnailMode !== 'empty' && (
        <div className='w-12 h-12'>
          {thumbnailMode === 'image' ? (
            <LandThumbnail land={land} className={'w-full h-full rounded object-cover'} />
          ) : (
            thumbnailMode === 'shape' && <LandShapeImage land={land} />
          )}
        </div>
      )}

      <div className='text-left text-base'>
        <span className='block leading-5 text-black dark:text-white font-bold whitespace-nowrap'>{land.name}</span>
        <span className='block text-gray-600 dark:text-white font-semibold text-sm whitespace-nowrap'>
          <TransLandCityStateText land={land} />
        </span>
        <LandMarketIndicator
          className='text-sm font-semibold text-gray-400'
          iconSize='sm'
          land={land}
          range={range}
          landMarketInfo={landMarketInfo}
          showDifference={false}
        />
      </div>

      <LandMarketPriceGraph land={land} graphRange={syntheticMarketDataRangeToGraphDateType(range)} size='small' />
    </div>
  );
};

export const LandIconInfoMarketTileLoading: FunctionComponent<
  Omit<LandIconInfoMarketTileProps, 'land' | 'landInfo' | 'range'> & ClassName
> = ({className, thumbnailMode = 'empty'}) => {
  return (
    <div className={`flex items-center gap-3 animate-pulse ${className}`}>
      {thumbnailMode !== 'empty' && <div className='w-10 h-10 bg-gray-300 rounded' />}
      <div className='h-full flex flex-col justify-around mr-1 text-left text-base'>
        <LoadingSection w={'w-20'} h={'h-4'} animate={false} />
        <LoadingSection w={'w-16'} h={'h-3'} animate={false} />

        <LoadingSection w={'w-[180px]'} h={'h-3'} animate={false} />
      </div>
      <div className={'w-28 h-12'}>
        <DateValueLineGraphLoadingSmall />
      </div>
    </div>
  );
};
